'use client'
import { useEffect } from 'react'
import { cloneElement, isValidElement, useState } from 'react'

import {
  flip,
  FloatingPortal,
  offset,
  OffsetOptions,
  Placement,
  shift,
  useClientPoint,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import clsx from 'clsx'

import { usePopoverStore } from '@/components/Popovers/PopoverStore'

export type TooltipProps = {
  content: string | React.ReactNode
  children: React.ReactNode
  closeDelay?: number
  offsetProp?: number | OffsetOptions
  openDelay?: number
  placement?: Placement | undefined
  shouldUseClientPoint?: boolean
  disabled?: boolean
  contentClassName?: string
  /** Allow tooltip to open even when a popover is already open (e.g. tooltips inside popover menus) */
  allowInPopover?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function Tooltip({
  content,
  children,
  closeDelay = 100,
  offsetProp = 8,
  openDelay = 100,
  placement = 'top',
  shouldUseClientPoint = false,
  disabled = false,
  contentClassName,
  allowInPopover = false,
  open: controlledOpen,
  onOpenChange,
}: TooltipProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const openId = usePopoverStore((s) => s.openId)

  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : uncontrolledOpen

  const handleOpenChange = (next: boolean) => {
    if (disabled) return
    // Guard opening when a popover is open, unless explicitly allowed.
    if (!allowInPopover && next && openId) return
    if (!isControlled) {
      setUncontrolledOpen(next)
    }
    if (onOpenChange) {
      onOpenChange(next)
    }
  }

  const { refs, floatingStyles, context } = useFloating({
    open: disabled ? false : open,
    onOpenChange: handleOpenChange,
    middleware: [offset(offsetProp), flip(), shift()],
    placement,
  })

  // Close tooltip if a popover opens, unless explicitly allowed.
  // Only notify when actually open — otherwise a controlled onOpenChange that
  // writes to external store + an unstable callback identity loops forever
  // (set → re-render → new onOpenChange → effect → onOpenChange(false) → …).
  useEffect(() => {
    if (!allowInPopover && openId && open) {
      if (!isControlled) {
        setUncontrolledOpen(false)
      }
      onOpenChange?.(false)
    }
  }, [allowInPopover, openId, open, isControlled, onOpenChange])

  const hover = useHover(context, {
    move: false,
    delay: { open: openDelay, close: closeDelay },
  })
  const focus = useFocus(context, {
    enabled: !disabled && !openId,
  })
  const dismiss = useDismiss(context, {
    enabled: !disabled,
  })
  const role = useRole(context, { role: 'tooltip' })
  const clientPoint = useClientPoint(context, {
    enabled: shouldUseClientPoint && !disabled,
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    clientPoint,
    hover,
    focus,
    dismiss,
    role,
  ])

  const child = isValidElement(children) ? children : <span>{children}</span>

  if (disabled) {
    return <>{children}</>
  }

  return (
    <>
      {cloneElement(child, {
        ref: refs.setReference,
        ...getReferenceProps(),
      })}

      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={`z-50 rounded-lg px-2 py-1 text-sm`}
            {...getFloatingProps()}
          >
            <div
              className={clsx(
                `bg-inverse-tertiary rounded-lg text-2xs/6 font-medium text-white dark:bg-zinc-700`,
                typeof content === 'string' && 'px-1.5',
                contentClassName,
              )}
            >
              {content}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
